import React, { useEffect, useState } from 'react';
import { useApi } from '../../hooks/useApi';
import { useClub } from '../../hooks/useClub';

const SECTIONS = [
  {
    key: 'profile',
    label: 'Profile',
    columns: [
      { key: 'email',       label: 'Email' },
      { key: 'phone',       label: 'Phone' },
      { key: 'sessionDate', label: 'PB101 Date' },
      { key: 'leadSource',  label: 'Lead Source' },
    ],
  },
  {
    key: 'pb101_event',
    label: 'PB101 Event',
    columns: [
      { key: 'openPlayInvited',  label: 'OP Invited',      render: v => v ? <span className="badge badge-green">Yes</span> : <span className="badge badge-gray">No</span> },
      { key: 'openPlayReserved', label: 'OP Reserved',     render: v => v ? <span className="badge badge-green">Yes</span> : <span className="badge badge-gray">No</span> },
      { key: 'attendedLtp',      label: 'Attended',        render: v => v ? <span className="badge badge-green">Yes</span> : <span className="badge badge-gray">No</span> },
      { key: 'stayedForPlay',    label: 'Stayed for Play', render: v => v ? <span className="badge badge-green">Yes</span> : v === false ? <span className="badge badge-gray">No</span> : null },
    ],
  },
  {
    key: 'day_of_ask',
    label: 'Day-of Trial Ask',
    columns: [
      { key: 'trialAskResult', label: 'Ask Result' },
      { key: 'status',         label: 'Outcome', render: v => <PB101StatusBadge status={v} /> },
    ],
  },
  {
    key: 'followup',
    label: 'Follow-Up & Conversion',
    columns: [
      { key: 'followupSent',       label: 'F/U Sent', render: v => v ? <span className="badge badge-green">Yes</span> : <span className="badge badge-gray">No</span> },
      { key: 'recurringConverted', label: 'Converted', render: v => v ? <span className="badge badge-green">Yes</span> : <span className="badge badge-gray">No</span> },
      { key: 'nextAction',         label: 'Next Action' },
      { key: 'notes',              label: 'Notes' },
    ],
  },
];

const ALL_COLUMNS = SECTIONS.flatMap(s => s.columns);

function buildKPIs(records) {
  const total     = records.length;
  const attended  = records.filter(r => r.attendedLtp).length;
  const converted = records.filter(r => r.recurringConverted).length;
  const asked     = records.filter(r => r.trialAskResult).length;
  const pct = n => total > 0 ? `${Math.round((n / total) * 100)}%` : '—';
  return [
    { label: 'Total Records',   value: total },
    { label: 'Attended',        value: attended },
    { label: '% Attended',      value: pct(attended) },
    { label: 'Trial Ask Made',  value: asked },
    { label: '% Ask Made',      value: pct(asked) },
    { label: 'Converted',       value: converted },
    { label: 'Conv. Rate',      value: pct(converted) },
  ];
}

export default function PB101Records({ onSelectRecord }) {
  const { get }         = useApi();
  const { currentClub } = useClub();
  const [records, setRecords]         = useState([]);
  const [loading, setLoading]         = useState(true);
  const [search, setSearch]           = useState('');
  const [activeSection, setActiveSection] = useState('all');

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    setLoading(true);
    get(`/pb101/records?club=${currentClub.id}`)
      .then(d => setRecords(d.records || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [currentClub.id]);

  const filtered = records.filter(r =>
    (r.name || '').toLowerCase().includes(search.toLowerCase()) ||
    (r.phone || '').includes(search) ||
    (r.email || '').toLowerCase().includes(search.toLowerCase())
  );

  const kpis = buildKPIs(filtered);

  const visibleColumns = activeSection === 'all'
    ? ALL_COLUMNS
    : (SECTIONS.find(s => s.key === activeSection)?.columns || ALL_COLUMNS);

  return (
    <div>
      <div style={styles.kpiBar}>
        {kpis.map(k => (
          <div key={k.label} style={styles.kpiItem}>
            <div style={styles.kpiValue}>{k.value}</div>
            <div style={styles.kpiLabel}>{k.label}</div>
          </div>
        ))}
      </div>

      <div style={{ marginBottom: '0.75rem' }}>
        <input
          className="field-input"
          placeholder="Search by name, email, or phone..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ maxWidth: 320 }}
        />
      </div>

      <div style={styles.tabBar}>
        <button
          style={{ ...styles.tab, ...(activeSection === 'all' ? styles.tabActive : {}) }}
          onClick={() => setActiveSection('all')}
        >
          All Columns
        </button>
        {SECTIONS.map(s => (
          <button
            key={s.key}
            style={{ ...styles.tab, ...(activeSection === s.key ? styles.tabActive : {}) }}
            onClick={() => setActiveSection(s.key)}
          >
            {s.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="empty-state"><div className="spinner" /></div>
      ) : (
        <div style={styles.tableWrapper}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={{ ...styles.th, ...styles.frozenCol, backgroundColor: '#1a1a1a' }}>Name</th>
                <th style={styles.th}>Date</th>
                {visibleColumns.map(col => (
                  <th key={col.key} style={styles.th}>{col.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={visibleColumns.length + 2} style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: '2rem' }}>
                    No records found
                  </td>
                </tr>
              )}
              {filtered.map(r => (
                <tr
                  key={r.id}
                  style={{ cursor: 'pointer' }}
                  onClick={() => onSelectRecord(r.id)}
                  className="table-row-hover"
                >
                  <td style={{ ...styles.td, ...styles.frozenCol, fontWeight: 500, backgroundColor: '#1a1a1a' }}>
                    {r.name}
                  </td>
                  <td style={styles.td}>
                    {r.sessionDate || <span style={{ color: 'var(--text-secondary)' }}>—</span>}
                  </td>
                  {visibleColumns.map(col => (
                    <td key={col.key} style={styles.td}>
                      {col.render
                        ? col.render(r[col.key], r)
                        : (r[col.key] !== undefined && r[col.key] !== null && r[col.key] !== '')
                          ? String(r[col.key])
                          : <span style={{ color: 'var(--text-secondary)' }}>—</span>
                      }
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ marginTop: '0.5rem', fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
        {filtered.length} record{filtered.length !== 1 ? 's' : ''}
        {search && ` matching "${search}"`}
      </div>
    </div>
  );
}

function PB101StatusBadge({ status }) {
  if (status === 'converted') return <span className="badge badge-green">Converted</span>;
  if (status === 'lost')      return <span className="badge badge-gray">Lost</span>;
  if (status === 'declined')  return <span className="badge badge-gray">Declined</span>;
  return <span className="badge badge-blue">Active</span>;
}

const styles = {
  kpiBar: {
    display: 'flex', gap: 0,
    background: '#141414', border: '1px solid var(--border)',
    borderRadius: '8px', overflow: 'hidden', marginBottom: '1rem',
  },
  kpiItem: {
    flex: 1, padding: '0.6rem 0.75rem',
    borderRight: '1px solid var(--border)',
    minWidth: 80, textAlign: 'center',
  },
  kpiValue: {
    fontSize: '1.1rem', fontWeight: 700,
    color: 'var(--text)', lineHeight: 1.2,
  },
  kpiLabel: {
    fontSize: '0.68rem', color: 'var(--text-secondary)',
    marginTop: '0.15rem', whiteSpace: 'nowrap',
  },
  tabBar: {
    display: 'flex', gap: '0.25rem',
    flexWrap: 'wrap', marginBottom: '0.6rem',
  },
  tab: {
    padding: '0.3rem 0.75rem', fontSize: '0.78rem',
    border: '1px solid var(--border)', borderRadius: '20px',
    background: 'transparent', color: 'var(--text-secondary)',
    cursor: 'pointer', whiteSpace: 'nowrap',
  },
  tabActive: {
    background: 'var(--yellow)', color: '#000',
    borderColor: 'var(--yellow)', fontWeight: 600,
  },
  tableWrapper: {
    overflowX: 'auto',
    background: '#141414', border: '1px solid var(--border)',
    borderRadius: '8px', position: 'relative',
  },
  table: {
    width: '100%', borderCollapse: 'collapse',
    fontSize: '0.83rem', minWidth: 600, tableLayout: 'auto',
  },
  th: {
    padding: '0.55rem 0.85rem', textAlign: 'left',
    fontSize: '0.72rem', fontWeight: 600,
    color: 'var(--text-secondary)', textTransform: 'uppercase',
    letterSpacing: '0.04em', borderBottom: '1px solid var(--border)',
    background: '#141414', whiteSpace: 'nowrap',
  },
  td: {
    padding: '0.55rem 0.85rem', borderBottom: '1px solid var(--border)',
    color: 'var(--text)', whiteSpace: 'nowrap',
    maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis',
  },
  frozenCol: {
    position: 'sticky', left: 0, zIndex: 2,
    background: '#1a1a1a',
    backgroundColor: '#1a1a1a',
    borderRight: '1px solid #333',
    minWidth: 140, boxShadow: '4px 0 8px rgba(0,0,0,0.8)',
  },
};
